import React, { useRef, useEffect, useState, useCallback } from "react";
import "./notificationPopup.css";

const NotificationPopup = ({
  visible,
  notifications,
  readNotifications,
  loadMoreNotifications,
  loadMoreReadNotifications,
  hasMore,
  hasMoreRead,
  markVisibleRead,
  unreadCount,
}) => {
  const [activeTab, setActiveTab] = useState("new");
  const newListRef = useRef(null);
  const readListRef = useRef(null);

  const isElementInView = (container, element) => {
    if (!container || !element) return false;
    const containerTop = container.scrollTop;
    const containerBottom = containerTop + container.clientHeight;
    const elemTop = element.offsetTop;
    const elemBottom = elemTop + element.clientHeight;
    return elemBottom >= containerTop && elemTop <= containerBottom;
  };

  const handleNewScroll = useCallback(() => {
    const container = newListRef.current;
    if (!container) return;

    if (
      container.scrollTop + container.clientHeight >=
      container.scrollHeight - 10
    ) {
      if (hasMore) loadMoreNotifications();
    }

    const unreadVisibleIds = [];
    notifications.forEach((notif) => {
      const elem = document.getElementById(`notif-${notif._id}`);
      if (elem && !notif.is_read && isElementInView(container, elem)) {
        unreadVisibleIds.push(notif._id);
      }
    });

    if (unreadVisibleIds.length > 0) markVisibleRead(unreadVisibleIds);
  }, [notifications, hasMore, loadMoreNotifications, markVisibleRead]);

  const handleReadScroll = useCallback(() => {
    const container = readListRef.current;
    if (!container) return;
    if (
      container.scrollTop + container.clientHeight >=
      container.scrollHeight - 10 &&
      hasMoreRead
    ) {
      loadMoreReadNotifications();
    }
  }, [hasMoreRead, loadMoreReadNotifications]);

  useEffect(() => {
    if (visible && activeTab === "new") {
      handleNewScroll();
    }
  }, [visible, notifications, activeTab]);

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hr ago`;
    const days = Math.floor(hrs / 24);
    return days === 1 ? "Yesterday" : `${days} days ago`;
  };

  const formatType = (type) => {
    if (!type) return "General";
    return type
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  if (!visible) return null;

  return (
    <div className="np-wrap">
      {/* Header */}
      <div className="np-header">
        <p className="np-title">Notifications</p>
        <div className="np-tabs">
          <button
            className={`np-tab ${activeTab === "new" ? "active" : ""}`}
            onClick={() => setActiveTab("new")}
          >
            New
            {unreadCount > 0 && (
              <span className="np-badge">{unreadCount}</span>
            )}
          </button>
          <button
            className={`np-tab ${activeTab === "read" ? "active" : ""}`}
            onClick={() => setActiveTab("read")}
          >
            Read
          </button>
        </div>
      </div>

      {/* New / Unread Tab */}
      {activeTab === "new" && (
        <div
          className="np-list"
          ref={newListRef}
          onScroll={handleNewScroll}
        >
          {notifications.length > 0 ? (
            <>
              {notifications
                .filter((notif) => notif.type !== "vr_home_visit_user" && notif.type_name !== "vr_home_visit_user")
                .map((notif) => (
                  <div
                    key={notif._id}
                    id={`notif-${notif._id}`}
                    className={`np-item ${!notif.is_read ? "unread" : ""}`}
                  >
                    <div className="np-row">
                      {!notif.is_read && <span className="np-dot" />}
                      <span className={`np-type ${notif.is_read ? "muted" : ""}`}>
                        {formatType(notif.type)}
                      </span>
                    </div>
                    {/* New Tab */}
                    <div className="np-msg-wrap">
                      <p className="np-msg">{notif.message}</p>
                      <span className="np-tooltip">{notif.message}</span>
                    </div>

                    <span className="np-time">{formatTime(notif.created_at)}</span>
                  </div>
                ))}
              {hasMore && (
                <div className="np-load">
                  <button onClick={loadMoreNotifications}>Load more</button>
                </div>
              )}
            </>
          ) : (
            <p className="np-empty">No new notifications</p>
          )}
        </div>
      )}

      {/* Read Tab */}
      {activeTab === "read" && (
        <div
          className="np-list"
          ref={readListRef}
          onScroll={handleReadScroll}
        >
          {readNotifications.length > 0 ? (
            <>
              {readNotifications
                .filter((notif) => notif.type !== "vr_home_visit_user" && notif.type_name !== "vr_home_visit_user")
                .map((notif) => (
                  <div key={notif._id} className="np-item">
                    <div className="np-row">
                      <span className="np-type muted">{formatType(notif.type || notif.type_name)}</span>
                    </div>
                    {/* Read Tab */}
                    <div className="np-msg-wrap">
                      <p className="np-msg">{notif.message}</p>
                      <span className="np-tooltip">{notif.message}</span>
                    </div>
                    <span className="np-time">{formatTime(notif.created_at)}</span>
                  </div>
                ))}
              {hasMoreRead && (
                <div className="np-load">
                  <button onClick={loadMoreReadNotifications}>Load more</button>
                </div>
              )}
            </>
          ) : (
            <p className="np-empty">No read notifications</p>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationPopup;